import { useCallback, useEffect, useMemo } from 'react';

import { Label } from '@eneco/dc/client/types';
import { typedYupResolver } from '@eneco/hooks/form';
import { DC_Repositories_Base_Enumerations_BusinessUnit } from '@monorepo-types/dc';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../../context';
import {
  getCrossSellRentalDeviceProductAvailability,
  getOffer,
  getUpsellProductAvailability,
  selectDefaultDiscountCode,
} from '../../../../shared/helpers';
import { handleNbaFeedback } from '../../../../shared/helpers/dc';
import { useFlowTracking, useFlowNavigation, useOfferMachine } from '../../../../shared/hooks';
import { type Offer, type OfferResponse } from '../../../../shared/types';

export interface FormFields {
  discountCode?: string;
}

interface UseOfferFormParams {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: Label;
  flowContext: FlowConsumerContextEnergy;
  offer?: OfferResponse;
  allOffers: Offer[];
  availableOffers: Offer[];
  validationMessage: string;
  autoReselectOnOffersChange: boolean;
  buildSubmitPayload?: (args: {
    getValues: () => FormFields;
    selectedDiscountCode?: string;
    flowContext: FlowConsumerContextEnergy;
    offer: ReturnType<typeof useOfferMachine>['offerState']['context']['offer'];
    allOffers: Offer[];
  }) => Record<string, unknown>;
}

export function useOfferForm({
  businessUnit,
  label,
  flowContext,
  offer,
  allOffers,
  availableOffers,
  validationMessage,
  autoReselectOnOffersChange,
  buildSubmitPayload,
}: UseOfferFormParams) {
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const { trackAddToCart } = useFlowTracking();

  const formSchema = useMemo(
    () =>
      yup.object({
        discountCode: yup.string().required(validationMessage),
      }),
    [validationMessage],
  );

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    resolver: typedYupResolver<FormFields>(formSchema),
    defaultValues: {},
  });

  const selectedDiscountCode = watch('discountCode');
  const selectedDiscountCodeIndex = Math.max(availableOffers?.findIndex(o => o.type === selectedDiscountCode) ?? 0, 0);

  // Auto-reselect discount code when available offers change and current selection is no longer valid
  useEffect(() => {
    if (!autoReselectOnOffersChange) return;
    if (!availableOffers.length || availableOffers?.some(o => o.type === selectedDiscountCode)) return;
    setValue('discountCode', selectDefaultDiscountCode(availableOffers));
  }, [autoReselectOnOffersChange, availableOffers, selectedDiscountCode, setValue]);

  const onSuccessfulSubmit: SubmitHandler<FormFields> = useCallback(
    formData => {
      const discountCode = formData.discountCode ?? selectedDiscountCode;
      const { hasCrossSellRentalDevice, hasUpsellGas } = flowContext;
      const isRentalDeviceCrossSellProductAvailable = getCrossSellRentalDeviceProductAvailability(offer);

      const isGasUpsellProductAvailable = getUpsellProductAvailability(
        'gas',
        { ...flowContext, discountCode },
        offer,
      );

      const selectedOffer = getOffer({ ...flowContext, discountCode }, offer);

      if (selectedOffer) trackAddToCart(selectedOffer, offer);
      if (selectedOffer?.nbaContext?.serviceLocationId && selectedOffer?.nbaContext?.contextId) {
        const { serviceLocationId, contextId, actionId, variationId, servingPointId } = selectedOffer.nbaContext;

        handleNbaFeedback(
          businessUnit,
          label,
          'Success',
          serviceLocationId,
          contextId,
          actionId,
          variationId,
          servingPointId,
        );
      }

      const basePayload = {
        ...getValues(),
        discountCode,
        isRentalDeviceCrossSellProductAvailable,
        isGasUpsellProductAvailable,
        hasCrossSellRentalDevice: hasCrossSellRentalDevice && isRentalDeviceCrossSellProductAvailable,
        hasUpsellGas: hasUpsellGas && isGasUpsellProductAvailable,
      };

      const customPayload = buildSubmitPayload
        ? buildSubmitPayload({
            getValues,
            selectedDiscountCode: discountCode,
            flowContext,
            offer,
            allOffers,
          })
        : {};

      updateContextAndGoToNextStep({
        ...basePayload,
        ...customPayload,
      });
    },
    [
      businessUnit,
      label,
      flowContext,
      offer,
      selectedDiscountCode,
      allOffers,
      getValues,
      buildSubmitPayload,
      updateContextAndGoToNextStep,
      trackAddToCart,
    ],
  );

  return {
    handleSubmit,
    control,
    getValues,
    setValue,
    errors,
    isSubmitSuccessful,
    selectedDiscountCode,
    selectedDiscountCodeIndex,
    onSuccessfulSubmit,
    updateContextAndGoToNextStep,
  };
}
